/**
 * Workspace resolution for the board, backlog and issue screens.
 *
 * The screens are mounted at several shapes of URL depending on how the shell
 * routes them (`/app/orgs/:slug/projects/:projectKey/board`, `/app/board`, a
 * modal over a list), so nothing here assumes a particular route. The org slug
 * comes from the path when it is there and from the authenticated session
 * otherwise; it is never read from a query string the user can edit into
 * another tenant's — and the server would refuse that anyway, since it resolves
 * the slug against the caller's own membership.
 */

import { useEffect, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { projectsApi } from '@/lib/api'
import { setCsrfToken } from '@/lib/csrf'
import type { Project, PublicUser, Role } from '@/types/api'

import { v2Api } from './api'
import { issueKeys } from './keys'
import type { MemberRow, OrganizationSummary } from './types'

const WRITE_ROLES: Role[] = ['owner', 'admin', 'member']

/**
 * The authenticated session.
 *
 * The response carries the CSRF token, which is handed to the shared token
 * store `@/lib/api` reads on every unsafe request — so a tab that opens
 * straight onto the board can write without waiting for the shell to read the
 * session first.
 */
export function useSession() {
  const query = useQuery({
    queryKey: issueKeys.session,
    queryFn: () => v2Api.session(),
    staleTime: 5 * 60_000,
    retry: false,
  })

  const token = query.data?.csrf_token
  useEffect(() => {
    if (token) setCsrfToken(token)
  }, [token])

  return query
}

export interface Workspace {
  /** null while the session is still loading and the path carries no slug. */
  slug: string | null
  organization: OrganizationSummary | null
  organizations: OrganizationSummary[]
  userId: number | null
  isLoading: boolean
  error: unknown
}

export function useWorkspace(): Workspace {
  const params = useParams()
  const routeSlug = params.slug ?? params.orgSlug ?? null
  const { data, isLoading, error } = useSession()

  const organizations = useMemo(() => data?.organizations ?? [], [data])

  const organization = useMemo(() => {
    if (organizations.length === 0) return null
    if (routeSlug) return organizations.find((org) => org.slug === routeSlug) ?? null
    if (data?.active_organization_id) {
      const active = organizations.find((org) => org.id === data.active_organization_id)
      if (active) return active
    }
    return organizations.find((org) => org.is_default) ?? organizations[0] ?? null
  }, [organizations, routeSlug, data?.active_organization_id])

  return {
    slug: routeSlug ?? organization?.slug ?? null,
    organization,
    organizations,
    userId: data?.user.id ?? null,
    isLoading,
    error,
  }
}

/** A project is addressable by its key (TSK) or, for legacy rows, its UUID. */
export function projectRef(project: Pick<Project, 'id' | 'project_key'>): string {
  return project.project_key ?? project.id
}

/**
 * The projects visible in this workspace.
 *
 * `/api/v2/orgs/{slug}/projects` does not exist yet, so this reads the frozen
 * `GET /api/projects` — a bare array, not a paginate envelope — and narrows it
 * to the active organization. Rows created before the tenancy backfill carry no
 * organization_id; they are kept rather than hidden.
 */
export function useProjects(organizationId?: string | null) {
  const query = useQuery({
    queryKey: ['v2', 'projects', 'legacy'],
    queryFn: () => projectsApi.list(),
    staleTime: 60_000,
  })

  const projects = useMemo(() => {
    const all = query.data ?? []
    if (!organizationId) return all
    const scoped = all.filter(
      (project) => !project.organization_id || project.organization_id === organizationId,
    )
    return scoped.length > 0 ? scoped : all
  }, [query.data, organizationId])

  return { ...query, projects }
}

export interface ProjectSelection {
  projectKey: string | null
  project: Project | null
  projects: Project[]
  isLoading: boolean
}

/**
 * The project the current screen is about: the path segment when the route
 * carries one, then `?project=`, then the workspace's default project. The
 * search param is what the board's own project switcher writes, so switching
 * project is a back-button-able navigation rather than hidden state.
 */
export function useProjectSelection(organizationId?: string | null): ProjectSelection {
  const params = useParams()
  const [search] = useSearchParams()
  const routeKey = params.projectKey ?? params.key ?? params.project ?? search.get('project') ?? null
  const { projects, isLoading } = useProjects(organizationId)

  const project = useMemo(() => {
    if (projects.length === 0) return null
    if (routeKey) {
      const wanted = routeKey.toLowerCase()
      const match = projects.find(
        (candidate) =>
          candidate.project_key?.toLowerCase() === wanted || candidate.id.toLowerCase() === wanted,
      )
      if (match) return match
    }
    return projects.find((candidate) => candidate.is_default) ?? projects[0] ?? null
  }, [projects, routeKey])

  return {
    projectKey: routeKey ?? (project ? projectRef(project) : null),
    project,
    projects,
    isLoading,
  }
}

export interface Roster {
  members: MemberRow[]
  byId: Map<number, PublicUser>
  role: Role | null
  canWrite: boolean
  isLoading: boolean
}

/**
 * The workspace roster, used for assignee pickers and @mention autocomplete.
 *
 * A viewer may read it — that is what makes the read-only board render names
 * instead of bare ids. `canWrite` mirrors the server's own rule so the UI hides
 * affordances the API would answer 403 to.
 */
export function useRoster(slug: string | null): Roster {
  const query = useQuery({
    queryKey: issueKeys.members(slug ?? ''),
    queryFn: () => v2Api.members(slug as string),
    enabled: Boolean(slug),
    staleTime: 5 * 60_000,
  })

  const members = useMemo(() => query.data?.data ?? [], [query.data])
  const byId = useMemo(() => {
    const map = new Map<number, PublicUser>()
    for (const member of members) {
      if (member.user) map.set(member.user_id, member.user)
    }
    return map
  }, [members])

  const role = query.data?.role ?? null
  return {
    members,
    byId,
    role,
    // Default to writable while the roster loads: the alternative flashes a
    // read-only board at every member on every navigation.
    canWrite: role === null ? true : WRITE_ROLES.includes(role),
    isLoading: query.isLoading,
  }
}

/** The org label registry, for the label picker and the filter bar. */
export function useLabels(slug: string | null) {
  return useQuery({
    queryKey: issueKeys.labels(slug ?? ''),
    queryFn: () => v2Api.labels(slug as string),
    enabled: Boolean(slug),
    staleTime: 5 * 60_000,
    select: (response) => response.data,
  })
}
